import { SkeletonLoader } from '@toggle/design-system';
import { Entity } from '@toggle/toggle';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { usePriceDisplay } from '~/hooks/use-price-display/usePriceDisplay';

import * as S from './InsightsLivePrice.styles';

/**
 * EarningsLivePrice Component
 *
 *  Determine which single price to show based on market conditions
 * - Market Open: Shows primary price with "Market Open" label
 * - Pre-market: Shows secondary price with "Pre-Market" tag
 * - After-hours: Shows secondary price with "After-hours" tag
 * - Fallback: Shows primary price (close) with "As of last close" text
 *
 */

export interface InsightsLivePriceProps {
  entity: Entity;
  isCardView?: boolean;
}

export const InsightsLivePrice = ({
  entity,
  isCardView,
}: InsightsLivePriceProps) => {
  const { t } = useTranslation(['analyze']);
  const { isLoading, isValid, priceData } = usePriceDisplay(entity);

  if (isLoading) {
    return (
      <div data-testid="insights-live-price-loading">
        <SkeletonLoader
          areas={[{ styles: { width: '120px', height: '40px' } }]}
          withWrapper={false}
        />
      </div>
    );
  }

  if (!isValid || !priceData) {
    return null;
  }

  const { price, mappedEntity, priceFormatter } = priceData;

  const getSinglePriceDisplay = () => {
    const marketHours = price.primary.marketHours;

    if (
      marketHours.isPreMarket &&
      price.secondary?.price !== undefined &&
      price.secondary?.isLive
    ) {
      return (
        <S.StyledPriceDisplay
          $isCardView={isCardView}
          price={priceFormatter(price.secondary.price)}
          currency={mappedEntity.currency}
          priceChange={price.secondary.priceChange}
          outsideHoursText={price.secondary.outsideHours}
          isPrimary={false}
          showMarketIndicator
        />
      );
    }

    if (
      marketHours.isAfterMarket &&
      price.secondary?.price !== undefined &&
      price.secondary?.isLive
    ) {
      return (
        <S.StyledPriceDisplay
          $isCardView={isCardView}
          price={priceFormatter(price.secondary.price)}
          currency={mappedEntity.currency}
          priceChange={price.secondary.priceChange}
          outsideHoursText={price.secondary.outsideHours}
          isPrimary={false}
          showMarketIndicator
        />
      );
    }

    if (marketHours.isMarketHours && price.primary?.isLive) {
      return (
        <S.StyledPriceDisplay
          $isCardView={isCardView}
          price={priceFormatter(price.primary.price ?? 0)}
          currency={mappedEntity.currency}
          priceChange={price.primary.priceChange}
          showMarketIndicator
          liveLabel={t('analyze:analysis.marketOpen')}
        />
      );
    }

    return (
      <S.StyledPriceDisplay
        $isCardView={isCardView}
        price={priceFormatter(price.primary.price ?? 0)}
        currency={mappedEntity.currency}
        priceChange={price.primary.priceChange}
        timeNode={
          <S.DateSpan $isCardView={isCardView}>
            {t('analyze:analysis.asOfLastClose')}
          </S.DateSpan>
        }
        isPrimary={false}
        showMarketIndicator
      />
    );
  };

  return <div data-testid="earnings-live-price">{getSinglePriceDisplay()}</div>;
};
